import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src={LOGO_URL} alt="Alignk Logo" className="h-12 w-auto" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
          Refund Policy
        </h1>
        <p className="text-gray-600 mb-8">Last Updated: January 2026</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              1. Overview
            </h2>
            <p className="text-gray-700 leading-relaxed">
              At Alignk, we strive to provide high-quality press release distribution services. This Refund Policy outlines the terms and conditions under which refunds may be issued.
            </p>
          </section>

          <section className="bg-red-50 border-l-4 border-red-400 p-6 my-6">
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  2. No Refunds for Non-Approved Content
                </h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed font-bold">
                    IMPORTANT: NO REFUNDS will be issued if websites or publications do not approve your submitted content.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>By purchasing our services, you explicitly acknowledge and agree that:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>All press releases are subject to editorial approval by third-party media outlets</li>
                    <li>Each website has independent editorial standards and approval processes</li>
                    <li>We submit your content to our network but cannot guarantee acceptance</li>
                    <li>Website rejection is not grounds for a refund</li>
                    <li>Alignk is not responsible for content rejection by publishing partners</li>
                    <li>Payment is for the service of content submission and facilitation, not for guaranteed publication</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed font-medium mt-3">
                    We make every effort to ensure your content meets quality standards before submission, but final approval remains at the sole discretion of the publishing websites.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              3. Eligible Refund Scenarios
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Refunds may be considered in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Technical Failure:</strong> If we fail to submit your content due to a technical error on our platform</li>
              <li><strong>Service Not Delivered:</strong> If we do not initiate the submission process within 5 business days of payment</li>
              <li><strong>Duplicate Charges:</strong> If you were accidentally charged multiple times for the same order</li>
              <li><strong>Unauthorized Charges:</strong> If you can provide proof of an unauthorized transaction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              4. Non-Refundable Circumstances
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Refunds will NOT be issued in the following cases:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Content rejected by publishing websites for any reason</li>
              <li>Lower-than-expected publication acceptance rates</li>
              <li>Delays in publication due to website editorial processes</li>
              <li>Dissatisfaction with the quality of websites that approved your content</li>
              <li>Change of mind after purchase</li>
              <li>Failure to provide required content or information in a timely manner</li>
              <li>Content that violates our content guidelines or website policies</li>
              <li>After the submission process has been initiated</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              5. Refund Request Process
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you believe you qualify for a refund under Section 3:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li>Contact our support team at <strong>support@alignk.com</strong> within 7 days of your purchase</li>
              <li>Provide your order number and detailed explanation of the issue</li>
              <li>Include any relevant documentation or evidence</li>
              <li>Our team will review your request within 5 business days</li>
              <li>If approved, refunds will be processed within 10 business days</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              6. Partial Refunds
            </h2>
            <p className="text-gray-700 leading-relaxed">
              In exceptional circumstances and at our sole discretion, we may offer partial refunds or service credits. This is evaluated on a case-by-case basis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              7. Refund Method
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Approved refunds will be issued to the original payment method used for purchase. Processing times may vary depending on your payment provider.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              8. Chargebacks
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Initiating a chargeback without first contacting our support team may result in immediate termination of your account and loss of access to our services. We encourage you to work with us directly to resolve any issues.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              9. Service Credits
            </h2>
            <p className="text-gray-700 leading-relaxed">
              In lieu of refunds, we may offer service credits that can be applied to future purchases. These credits are non-transferable and must be used within 12 months of issuance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              10. Quality Commitment
            </h2>
            <p className="text-gray-700 leading-relaxed">
              While we cannot guarantee publication acceptance, we are committed to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Reviewing your content for quality before submission</li>
              <li>Submitting to reputable, relevant publications</li>
              <li>Providing transparent reporting on submission status</li>
              <li>Offering guidance to improve content for better acceptance rates</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              11. Policy Changes
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting on this page. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              12. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed">
              For refund requests or questions about this policy, please contact:
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Email:</strong> support@alignk.com<br />
              <strong>Subject Line:</strong> Refund Request - [Your Order Number]
            </p>
          </section>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-8">
            <p className="text-gray-700 leading-relaxed font-medium">
              <strong>Important Reminder:</strong> By completing your purchase, you confirm that you have read, understood, and agree to this Refund Policy, including the no-refund policy for content that is not approved by publishing websites.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;